import { useEffect, useRef } from 'react';

const LOCAL_MAP_KEY = 'app_open_tabs_map';      // localStorage: { tabId: lastHeartbeatMs, ... }
const PENDING_CLEAR_KEY = 'app_auth_clear_pending'; // localStorage: timestamp when clear was requested
const HEARTBEAT_INTERVAL = 1000;   // ms
const STALE_TIMEOUT = 4000;        // ms - consider a tab gone if no heartbeat within this time
const CLEAR_GRACE = 2000;          // ms - wait this long after seeing zero tabs before actually removing auth
const PRUNE_INTERVAL = 1000;       // ms - how often to prune & check

export const useTabClose = () => {
  const tabIdRef = useRef(null);
  const hbRef = useRef(null);
  const pruneRef = useRef(null);

  const readMap = () => {
    try {
      const raw = localStorage.getItem(LOCAL_MAP_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  };

  const writeMap = (map) => {
    try {
      localStorage.setItem(LOCAL_MAP_KEY, JSON.stringify(map));
    } catch {}
  };

  const setPendingClear = (ts) => {
    try {
      localStorage.setItem(PENDING_CLEAR_KEY, String(ts));
    } catch {}
  };

  const clearPendingClear = () => {
    try {
      localStorage.removeItem(PENDING_CLEAR_KEY);
    } catch {}
  };

  const heartbeat = () => {
    const id = tabIdRef.current;
    if (!id) return;
    const map = readMap();
    map[id] = Date.now();
    // prune stale in-memory
    const now = Date.now();
    Object.keys(map).forEach((k) => {
      if (now - map[k] > STALE_TIMEOUT) delete map[k];
    });
    writeMap(map);
    // if we have any tab now, cancel any pending clear
    if (Object.keys(map).length > 0) clearPendingClear();
  };

  // prune stale entries, set pending clear if map empty, and actually clear auth after grace
  const pruneAndMaybeClearAuth = () => {
    const map = readMap();
    const now = Date.now();
    let changed = false;
    Object.keys(map).forEach((k) => {
      if (now - map[k] > STALE_TIMEOUT) {
        delete map[k];
        changed = true;
      }
    });
    if (changed) writeMap(map);

    const keysLeft = Object.keys(map).length;
    if (keysLeft === 0) {
      const pending = localStorage.getItem(PENDING_CLEAR_KEY);
      if (!pending) {
        // first time we see zero tabs -> start pending timer
        setPendingClear(now);
        return;
      }
      const pendingTs = Number(pending) || 0;
      if (now - pendingTs >= CLEAR_GRACE) {
        // grace expired and still no tabs -> final clear
        localStorage.removeItem('auth_token');
        // clear the pending flag too
        clearPendingClear();
        // also clear the map just in case
        try { localStorage.removeItem(LOCAL_MAP_KEY); } catch {}
      }
    } else {
      // there are active tabs -> ensure no pending clear remains
      clearPendingClear();
    }
  };

  useEffect(() => {
    // create / reuse tabId across reloads
    let tabId = sessionStorage.getItem('tabId');
    if (!tabId) {
      tabId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem('tabId', tabId);
    }
    tabIdRef.current = tabId;

    // immediate heartbeat
    heartbeat();

    // start heartbeat & pruning timers
    hbRef.current = setInterval(heartbeat, HEARTBEAT_INTERVAL);
    pruneRef.current = setInterval(pruneAndMaybeClearAuth, PRUNE_INTERVAL);

    // respond to storage changes from other tabs
    const onStorage = (e) => {
      if (!e.key || [LOCAL_MAP_KEY, PENDING_CLEAR_KEY].includes(e.key)) {
        // some other tab updated map or pending flag -> re-evaluate
        pruneAndMaybeClearAuth();
      }
    };
    window.addEventListener('storage', onStorage);

    // Do NOT remove our entry on unload/pagehide to avoid races with reload.
    // We rely on heartbeat + stale pruning + pending-clear grace to do final cleanup.

    return () => {
      // normal React unmount (SPA navigation) - remove our entry now so others see it
      clearInterval(hbRef.current);
      clearInterval(pruneRef.current);
      window.removeEventListener('storage', onStorage);

      try {
        const map = readMap();
        if (map[tabIdRef.current]) {
          delete map[tabIdRef.current];
          writeMap(map);
        }
      } catch {}
      pruneAndMaybeClearAuth();
    };
  }, []);
};
